import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { PackageOrderSummary } from "@/features/Packages/PackageOrderSummary";
import { PaymentMethod } from "@/features/Square/PaymentMethod";
import { SelectPaymentModal } from "@/features/Square/SelectPaymentModal";
import { useCreateUserCardOnFile } from "@/features/Square/SquareCardEntryModal/hooks/useCreateUserCardOnFile";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useAssignPaymentToPackage } from "@whoosh/features/src/Packages";
import { SquareCard } from "@whoosh/features/src/Square/features/SquareCardEntry/types";

import { StorePackageSummary } from "./StorePackageModal/components/StorePackageSummary";
import { StoreProductOrder_memberPackage$key } from "./__generated__/StoreProductOrder_memberPackage.graphql";
import { StoreProductOrder_order$key } from "./__generated__/StoreProductOrder_order.graphql";

type Props = {
  memberPackageRef?: StoreProductOrder_memberPackage$key | null;
  orderRef?: StoreProductOrder_order$key | null;
};

export const StoreProductOrder = ({ memberPackageRef, orderRef }: Props) => {
  const { showToast } = useToast();
  const [isPaymentMethodSelectModalOpen, setIsPaymentMethodSelectModalOpen] =
    useState(false);

  const memberPackage = useFragment(
    StoreProductOrderMemberPackageFragmentString,
    memberPackageRef
  );
  const order = useFragment(StoreProductOrderFragmentString, orderRef);

  const customerId = memberPackage?.customer?.id || "";

  const [createSquareCard] = useCreateUserCardOnFile();
  const [assignPaymentMethodToPackage] = useAssignPaymentToPackage();

  const handlePaymentMethodSelected = (paymentMethodId: string) => {
    if (memberPackage) {
      assignPaymentMethodToPackage(
        { id: memberPackage.id || "", squareCardId: paymentMethodId },
        () => {
          setIsPaymentMethodSelectModalOpen(false);
        },
        (error: any) => {
          setTimeout(() => {
            showToast({
              title: t`Error`,
              description: `${t`Unable to add payment method`} - ${
                error.message
              }`,
              semanticStyle: "error",
            });
          }, 10);
        }
      );
    }
  };

  const handleAddNewCard = (squareCard: SquareCard): Promise<string> =>
    createSquareCard({ squareNonce: squareCard.nonce });

  return (
    <>
      <VStack px={6} gap={8}>
        <StorePackageSummary
          packageRef={memberPackage?.package}
          hideLabel
          hidePrices
        />
        <PackageOrderSummary orderRef={order} />
        <PaymentMethod
          squareCardRef={memberPackage?.squareCard}
          onAddPress={() => {
            setIsPaymentMethodSelectModalOpen(true);
          }}
          onChangePress={() => {
            setIsPaymentMethodSelectModalOpen(true);
          }}
        />
      </VStack>
      {isPaymentMethodSelectModalOpen ? (
        <SelectPaymentModal
          customerId={customerId}
          isOpen={isPaymentMethodSelectModalOpen}
          onClose={() => setIsPaymentMethodSelectModalOpen(false)}
          onAddNewCard={handleAddNewCard}
          onSelectPaymentMethod={handlePaymentMethodSelected}
        />
      ) : null}
    </>
  );
};

const StoreProductOrderMemberPackageFragmentString = graphql`
  fragment StoreProductOrder_memberPackage on MemberPackage {
    id
    customer {
      id
    }
    squareCard {
      ...PaymentMethod_squareCardOnFile
    }
    package {
      ...usePackageDisplayName_package
      ...StorePackageSummary_package
    }
  }
`;

const StoreProductOrderFragmentString = graphql`
  fragment StoreProductOrder_order on Order {
    ...PackageOrderSummary_order
  }
`;
